import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  getElementById,
  getContainerClass,
  getLabelStyles,
} from "../utils/styleUtils";

export const CustomMultiSelect = ({ instanceId, field, form, ...props }) => {
  const formBuilder = useSelector((state) => state.customFormBuilder);
  const [selectedValues, setSelectedValues] = useState([]);

  const element = getElementById(instanceId, formBuilder);

  // Initialize with field value if it exists
  useEffect(() => {
    if (field?.value) {
      if (typeof field.value === "string" && field.value.trim() !== "") {
        // Parse comma-separated string to array
        setSelectedValues(field.value.split(",").map((item) => item.trim()));
      } else if (Array.isArray(field.value)) {
        setSelectedValues(field.value);
      }
    }
  }, [field?.value]);

  if (!element) {
    return <div>multiSelect element not found</div>;
  }

  const elementStyle = element.style || {};
  const labelPosition = elementStyle.labelPosition || "top";

  // Helper function to convert array to comma-separated string
  const arrayToCommaSeparatedString = (array) => {
    return array.join(", ");
  };

  const getCheckboxContainerStyles = () => {
    return {
      border: `1px ${elementStyle.borderStyle || "solid"} ${
        elementStyle.borderColor || "#ced4da"
      }`,
      borderRadius: elementStyle.borderRadius || "0.375rem",
      padding: elementStyle.padding || "0.5rem",
      backgroundColor: elementStyle.backgroundColor || "#FFFFFF",
      maxHeight: elementStyle.maxHeight || "250px",
      overflowY: "auto",
      width: "100%",
      boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.075)",
    };
  };

  const getCheckboxStyles = () => {
    return {
      width: elementStyle.checkboxSize || "1rem",
      height: elementStyle.checkboxSize || "1rem",
      accentColor: elementStyle.checkboxColor || "#0d6efd",
      marginRight: "0.5rem",
      cursor: "pointer",
    };
  };

  const getOptionLabelStyles = () => {
    return {
      fontSize: elementStyle.optionFontSize || "0.875rem",
      color: elementStyle.optionTextColor || "#495057",
      cursor: "pointer",
    };
  };

  const handleCheckboxChange = (option, isChecked) => {
    let newSelectedValues;

    if (isChecked) {
      // Add to selected values
      newSelectedValues = [...selectedValues, option];
    } else {
      // Remove from selected values
      newSelectedValues = selectedValues.filter((value) => value !== option);
    }

    // Update local state
    setSelectedValues(newSelectedValues);

    // Update form field value as comma-separated string
    if (field && form) {
      const commaSeparatedString =
        arrayToCommaSeparatedString(newSelectedValues);
      form.setFieldValue(field.name, commaSeparatedString);
    }
  };

  const handleSelectAllChange = (isChecked) => {
    const options = elementStyle.options || [
      "Option 1",
      "Option 2",
      "Option 3",
    ];
    let newSelectedValues = isChecked ? [...options] : [];

    // Update local state
    setSelectedValues(newSelectedValues);

    // Update form field value as comma-separated string
    if (field && form) {
      const commaSeparatedString =
        arrayToCommaSeparatedString(newSelectedValues);
      form.setFieldValue(field.name, commaSeparatedString);
    }
  };

  const options = elementStyle.options || ["Option 1", "Option 2", "Option 3"];
  const showSelectAll = elementStyle.showSelectAll !== false;

  // Get the current comma-separated string for display
  const selectedValuesString = arrayToCommaSeparatedString(selectedValues);

  return (
    <div className={getContainerClass(labelPosition)} style={{ width: "100%" }}>
      {elementStyle.label?.label && (
        <label style={getLabelStyles(elementStyle, labelPosition)}>
          {elementStyle.label.label}
          {elementStyle.isRequired && (
            <span style={{ color: "#dc3545", marginLeft: "0.2rem" }}>*</span>
          )}
        </label>
      )}

      <div className="d-flex flex-column w-100">
        <div style={getCheckboxContainerStyles()}>
          {/* Select All Checkbox */}
          {showSelectAll && options.length > 1 && (
            <div className="d-flex align-items-center p-2 border-bottom mb-2">
              <input
                type="checkbox"
                id={`select-all-${instanceId}`}
                checked={selectedValues.length === options.length}
                onChange={(e) => handleSelectAllChange(e.target.checked)}
                style={getCheckboxStyles()}
              />
              <label
                htmlFor={`select-all-${instanceId}`}
                className="mb-0 fw-bold"
                style={getOptionLabelStyles()}
              >
                Select All
              </label>
            </div>
          )}

          {/* Options as checkboxes */}
          {options.map((option, index) => (
            <div
              key={index}
              className="d-flex align-items-center p-2"
              style={{
                borderRadius: "0.25rem",
                transition: "background-color 0.2s",
                backgroundColor: selectedValues.includes(option)
                  ? "rgba(13, 110, 253, 0.05)"
                  : "transparent",
                marginBottom: "0.25rem",
              }}
            >
              <input
                type="checkbox"
                id={`checkbox-${instanceId}-${index}`}
                checked={selectedValues.includes(option)}
                onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                style={getCheckboxStyles()}
                required={
                  elementStyle.isRequired &&
                  index === 0 &&
                  selectedValues.length === 0
                }
              />
              <label
                htmlFor={`checkbox-${instanceId}-${index}`}
                className="mb-0 w-100"
                style={getOptionLabelStyles()}
              >
                {option}
              </label>
            </div>
          ))}
        </div>

        {/* Selected values display */}
        {elementStyle.showSelectedCount !== false && (
          <div className="mt-1">
            <small className="text-muted" style={{ fontSize: "0.75rem" }}>
              {selectedValues.length} of {options.length} selected
            </small>
            {selectedValues.length > 0 && (
              <div className="mt-1">
                <small className="text-info" style={{ fontSize: "0.75rem" }}>
                  Selected: {selectedValuesString}
                </small>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
